import type {
  SubtitleEditorData,
  SubtitleEditorLogoData,
} from '@/components/SubtitleEditor';
import { resolveRestoredBrandKitId } from '@/lib/brand-kit/brandKitAccess';
import {
  buildEnforcedInitialConfig,
  type EnforcedPostLayout,
} from '@/lib/layouts/applyEnforcedLayout';
import type { LayoutFieldBinds } from '@/lib/layouts/layoutFieldBinds';
import { parsePersistedPostLayout } from '@/lib/layouts/persistPostLayout';
import type { PostLayoutConfig } from '@/lib/layouts/types';

interface PersistedProjectPreview {
  subtitle_config?: unknown;
  brand_kit?: unknown;
  speaker_layout?: unknown;
  post_layout?: unknown;
}

export interface ResolvedPersistedProjectPreviewSettings {
  subtitleData: SubtitleEditorData;
  logoData: SubtitleEditorLogoData;
  fontId: string;
  brandKitId: string | null;
  speakerLayout: number;
  postLayout: PostLayoutConfig | null;
  fieldBinds: LayoutFieldBinds;
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const positionFromAlignItems = (alignItems: string): string => {
  if (alignItems === 'flex-start') return 'top-center';
  if (alignItems === 'center') return 'middle-center';
  return 'bottom-center';
};

export function resolvePersistedProjectPreviewSettings({
  project,
  brandKits,
  hasProFeatures,
  defaultSubtitleData,
  defaultLogoData,
  effectivePostLayout,
  effectiveSubtitlePosition,
}: {
  project: PersistedProjectPreview | null | undefined;
  brandKits: Array<{ id: string; is_public?: boolean | null }>;
  hasProFeatures: boolean;
  defaultSubtitleData: SubtitleEditorData;
  defaultLogoData: SubtitleEditorLogoData;
  effectivePostLayout: EnforcedPostLayout | null | undefined;
  effectiveSubtitlePosition?: {
    alignItems: string;
    verticalMargin: number;
  } | null;
}): ResolvedPersistedProjectPreviewSettings {
  const subtitleConfig = asRecord(project?.subtitle_config);
  const savedSubtitle = asRecord(subtitleConfig?.subtitle);
  const savedLogo = asRecord(subtitleConfig?.logo);
  const brandKit = asRecord(project?.brand_kit);
  const parsed = parsePersistedPostLayout(project?.post_layout);
  const persistedPostLayout = parsed?.postLayout ?? null;
  const subtitleData = {
    ...defaultSubtitleData,
    ...(savedSubtitle ?? {}),
    ...(effectiveSubtitlePosition
      ? {
        position: positionFromAlignItems(effectiveSubtitlePosition.alignItems),
        marginVertical: effectiveSubtitlePosition.verticalMargin,
      }
      : {}),
  } as SubtitleEditorData;

  const speakerLayout = (
    typeof project?.speaker_layout === 'number'
    && Number.isInteger(project.speaker_layout)
    && project.speaker_layout > 0
  )
    ? project.speaker_layout
    : 1;

  return {
    subtitleData,
    logoData: {
      ...defaultLogoData,
      ...(savedLogo ?? {}),
    } as SubtitleEditorLogoData,
    fontId: typeof subtitleConfig?.font_id === 'string'
      ? subtitleConfig.font_id
      : '',
    brandKitId: resolveRestoredBrandKitId({
      hasProFeatures,
      brandKitId: typeof brandKit?.id === 'string' ? brandKit.id : null,
      kits: brandKits,
    }),
    speakerLayout,
    postLayout: effectivePostLayout
      ? buildEnforcedInitialConfig(
        effectivePostLayout,
        persistedPostLayout
          ? persistedPostLayout as unknown as { kind: string } & Record<string, unknown>
          : null,
      )
      : persistedPostLayout,
    fieldBinds: parsed?.fieldBinds ?? {},
  };
}
